import React, {useEffect, useState} from "react";
import {
    Box,
    Button,
    CardActions,
    CardContent,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Typography
} from "@mui/material";
import {fetchCurrencies, selectCurrencies} from "../../store/currencySlice";
import {useDispatch, useSelector} from "react-redux";
import WalletBalance from "../layout/WalletBalance";
import {deleteBudget, saveBudget} from "../../store/budgetSlice";
import {selectUserSettings} from "../../store/userSettingsSlice";
import BalanceNumericFormat from "../utils/BalanceNumericFormat";
import NumberService from "../../services/NumberService";
import BooleanService from "../../services/BooleanService";
import DateService from "../../services/DateService";
import EditChangeBudgetForm from "./EditChangeBudgetForm";
import ViewAndEditFormLayout from "./ViewAndEditFormLayout";
import {useTranslation} from "react-i18next";


const EditBudgetForm = ({budget, onCancel}) => {
    const {t} = useTranslation();
    const DEFAULT_RULE_ERROR = {
            startDate: null,
            endDate: null,
            time: null,
            dailyRule: null,
            weeklyRule: null,
            monthlyRule: null,
            ruleType: null
        };

    const DEFAULT_ERROR = {
        name: null,
        refill: null,
        currency: null,
        rule: {...DEFAULT_RULE_ERROR}};

    const DAYS_OF_WEEK = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

    const RULE_TYPES = {
            TYPE_ONE_TIME: "TYPE_ONE_TIME",
            TYPE_DAILY: "TYPE_DAILY",
            TYPE_WEEKLY: "TYPE_WEEKLY",
            TYPE_MONTHLY: "TYPE_MONTHLY"
        }



    const dispatch = useDispatch();
    const userSettings = useSelector(selectUserSettings);
    const defaultCurrencyId = userSettings?.defaultCurrencyId || null;
    const [name, setName] = useState(budget.name);
    const [refill, setRefill] = useState(budget.refill);
    const [currencyId, setCurrencyId] = useState(budget.currency || (budget.id ? null : defaultCurrencyId));
    const [balances, setBalances] = useState(budget.balances);
    const [errors, setErrors] = useState({...DEFAULT_ERROR});
    const [startDate, setStartDate] = useState(DateService.getTodayLocalDateString());
    const [endDate, setEndDate] = useState(null);
    const [ruleTime, setRuleTime] = useState("07:30");
    const [ruleType, setRuleType] = useState(RULE_TYPES.TYPE_ONE_TIME);
    const [dailyRule, setDailyRule] = useState("1");
    const [weeklyRule, setWeeklyRule] = useState(BooleanService.splitWeeklyCalendarDowToBooleanArray(""));
    const [monthlyRule, setMonthlyRule] = useState(BooleanService.splitToBooleanArray("", 31));
    const [changeBalanceTransaction, setChangeBalanceTransaction] = useState(null);
    const currencies = useSelector(state => selectCurrencies(state));

    // const [childResetTrigger, setResetTrigger] = useState(Date.now());

    const normalizeRuleTime = (time) => {
        if (!time || typeof time !== "string") {
            return "07:30";
        }
        return time.length >= 5 ? time.substring(0, 5) : "07:30";
    };

    useEffect(() => {
        setName(budget.name);
        setCurrencyId(budget.currency || (budget.id ? null : defaultCurrencyId));
        setBalances(budget.balances);
        setRefill(budget.refill);
        setChangeBalanceTransaction(null);

        const rule = budget.rule || budget.repeatRule
        if(rule) {
            const start = rule.startDate
                ? (typeof rule.startDate === "string" ? rule.startDate.substring(0, 10) : null)
                : null;
            const end = rule.finalDate
                ? (typeof rule.finalDate === "string" ? rule.finalDate.substring(0, 10) : null)
                : (rule.endDate ? (typeof rule.endDate === "string" ? rule.endDate.substring(0, 10) : null) : null);
            setStartDate(start || DateService.getTodayLocalDateString());
            setEndDate(end ?? null);
            setRuleTime(normalizeRuleTime(rule.time));
            setRuleType(rule.repeatType === "TYPE_SIMPLE" ? RULE_TYPES.TYPE_ONE_TIME : rule.repeatType);
            switch (rule.repeatType) {
                case RULE_TYPES.TYPE_ONE_TIME:
                    break;
                case RULE_TYPES.TYPE_DAILY:
                    setDailyRule("" + rule.selectedIndex);
                    break;
                case RULE_TYPES.TYPE_WEEKLY:
                    setWeeklyRule(BooleanService.splitWeeklyCalendarDowToBooleanArray(rule.selectedIndex));
                    break;
                case RULE_TYPES.TYPE_MONTHLY:
                    setMonthlyRule(BooleanService.splitToBooleanArray(rule.selectedIndex, 31));
                    break;
            }
        } else {
            setRuleTime("07:30");
        }

        setErrors({...DEFAULT_ERROR})

    }, [budget]);

    useEffect(() => {
        dispatch(fetchCurrencies())
    }, []);

    useEffect(() => {
        if (!budget.id && !currencyId && defaultCurrencyId) {
            setCurrencyId(defaultCurrencyId);
        }
    }, [defaultCurrencyId]);

    const onSave = () => {
        if (validateForm()) {
            const selectedIndex =
                ruleType === RULE_TYPES.TYPE_DAILY ? "" + dailyRule :
                ruleType === RULE_TYPES.TYPE_WEEKLY ? BooleanService.toWeeklyCalendarDowCommaSeparated(weeklyRule) :
                ruleType === RULE_TYPES.TYPE_MONTHLY ? BooleanService.toCommaSeparated(monthlyRule) : null;

            const result = budget.id ? "view" : "close";
            return new Promise((resolve) => {
                dispatch(saveBudget({
                    ...budget,
                    name: name,
                    refill: refill,
                    currency: currencyId ? currencyId : null,
                    rule: {
                        startDate: startDate,
                        finalDate: endDate || null,
                        time: ruleTime,
                        repeatType: ruleType,
                        selectedIndex: selectedIndex
                    }
                }, () => resolve(result)));
            });
        }
    }

    const getRuleTypeLabel = (ruleType) => {
        switch (ruleType) {
            case RULE_TYPES.TYPE_ONE_TIME:
                return t("forms.budget.repeatTypes.simple");
            case RULE_TYPES.TYPE_DAILY:
                return t("forms.budget.repeatTypes.daily");
            case RULE_TYPES.TYPE_WEEKLY:
                return t("forms.budget.repeatTypes.weekly");
            case RULE_TYPES.TYPE_MONTHLY:
                return t("forms.budget.repeatTypes.monthly");
            default: return ""
        }
    }

    const onNameChange = (event) => {
        setName(event.target.value);
        setErrors({...errors, name: null});
    }

    const onCurrencyChange = (event) => {
        setCurrencyId(event.target.value)
        setErrors({...errors, currency: null});
    }

    const onRefillChange = (event) => {
        setRefill(event.target.value)
        setErrors({...errors, refill: null});
    }

    const onChangeBalance = (event) => {
        setChangeBalanceTransaction({date: new Date(), budget: budget ? budget.id : null, changes: [{currency: budget.currency, value: 0}]})
    }

    const validateForm = () => {
        const refillValue = refill === null || refill === undefined ? "" : `${refill}`.trim();
        const hasDateRangeError = !!(startDate && endDate && endDate < startDate);
        const ruleErrors = {
            startDate: !startDate ? "Start date is required" : null,
            endDate: !endDate
                ? "End date is required"
                : (hasDateRangeError ? "End date must be on or after start date" : null),
            time: !ruleTime ? "Time is required" : null,
            ruleType : !ruleType ? "Rule type is required" : null,
            dailyRule: ruleType === RULE_TYPES.TYPE_DAILY && !dailyRule ? "Please select period" : null,
            weeklyRule: ruleType === RULE_TYPES.TYPE_WEEKLY && !weeklyRule.includes(true) ? "Please choose at least one day" : null,
            monthlyRule: ruleType === RULE_TYPES.TYPE_MONTHLY && !monthlyRule.includes(true)  ? "Please choose at least one date" : null
        }

        const newErrors = {
            name: !name.trim() ? "Name is required" : null,
            refill: !refillValue ? "Refill is required" : null,
            currency: !currencyId ? "Currency is not selected" : null,
            rule: ruleErrors
        }
        setErrors(newErrors);
        return !newErrors.name
            && !newErrors.refill
            && !newErrors.currency
            && !Object.values(ruleErrors).some(Boolean);
    }

    const onDelete = (event) => {
        dispatch(deleteBudget(budget, () => onCancel()))
    }

    const changeDailyRule = (day) => {
        setDailyRule(day)

        setErrors({...errors, rule: {...DEFAULT_RULE_ERROR}});
    }

    const changeWeeklyRule = (day) => {
        const newWeeklyRule = [...weeklyRule];
        newWeeklyRule[day] = !newWeeklyRule[day];
        setWeeklyRule(newWeeklyRule)

        setErrors({...errors, rule: {...DEFAULT_RULE_ERROR}});
    }

    const changeMonthlyRule= (day) => {
        const newMonthlyRule = [...monthlyRule];
        newMonthlyRule[day] = !newMonthlyRule[day];
        setMonthlyRule(newMonthlyRule);

        setErrors({...errors, rule: {...DEFAULT_RULE_ERROR}});
    }

    const changeType = (newType) => {
        if(ruleType !== newType) {
            setRuleType(newType);
            setErrors({...errors, rule: {...DEFAULT_RULE_ERROR}});
        }
    }

    const getRepeatRuleSummary = () => {
        switch (ruleType) {
            case RULE_TYPES.TYPE_DAILY: {
                const days = dailyRule || "1";
                return `Daily: every ${days} day(s)`;
            }
            case RULE_TYPES.TYPE_WEEKLY: {
                const ruleSelectedIndex = budget?.rule?.selectedIndex ?? budget?.repeatRule?.selectedIndex;
                const weeklySource = ruleSelectedIndex
                    ? BooleanService.splitWeeklyCalendarDowToBooleanArray(ruleSelectedIndex)
                    : weeklyRule;
                const selectedDays = weeklySource
                    .map((selected, i) => (selected ? DAYS_OF_WEEK[i] : null))
                    .filter(Boolean)
                    .join(", ");
                return `Weekly - ${selectedDays || "None"}`;
            }
            case RULE_TYPES.TYPE_MONTHLY: {
                const selectedDates = monthlyRule
                    .map((selected, i) => (selected ? i + 1 : null))
                    .filter(Boolean)
                    .join(", ");
                return `Monthly - ${selectedDates || "None"}`;
            }
            case RULE_TYPES.TYPE_ONE_TIME:
            default:
                return "No repeat";
        }
    }


    const viewContent = (
        <>
            <CardContent>
                <Box
                    sx={{
                        width: '93%',
                        backgroundColor: '#f5f5f5',
                        border: 1,
                        borderColor: 'primary.light',
                        borderRadius: 1,
                        p: 2,
                        mb: 2,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}
                >
                    <Typography variant="h6">{t("common.labels.balance")}</Typography>
                    <Typography variant="h4" sx={{ fontWeight: 500 }}>
                        {balances && balances.map(balance => (
                            <Box key={balance.currency || "null"}><WalletBalance wallet={balance} /></Box>
                        ))}
                    </Typography>
                </Box>
                <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                    <Typography variant="subtitle1">
                        {ruleType === RULE_TYPES.TYPE_ONE_TIME ? t("common.labels.amount") : t("forms.budget.refillAmount")}
                    </Typography>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        <WalletBalance wallet={{ currency: budget.currency, value: refill }} />
                    </Typography>
                </Box>
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                    <Typography variant="subtitle1">{t("forms.budget.repeatRule")}</Typography>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        {getRepeatRuleSummary()}
                    </Typography>
                </Box>
            </CardContent>

            <CardActions sx={{ ml: "2rem", mb: "1rem", mt: "1rem", mr: "2rem", height: "4rem" }}>
                <Button variant="outlined"  sx={{ height: '50px', lineHeight: 1.2 }} onClick={onCancel}>{t("common.buttons.close")}</Button>
                <Button variant="contained"  sx={{ height: '50px', lineHeight: 1.2 }} onClick={onChangeBalance}>{t("common.buttons.changeBalance")}</Button>
            </CardActions>
        </>
    );

    const editContent = ({ onCancel: handleCancel, onSave: handleSaveLayout }) => (
        <>
            <CardContent>
                <Box sx={{ m: 2 }}>
                    <TextField id="name" value={name}
                               onChange={onNameChange}
                               label={t("common.labels.name")}
                               variant="standard" sx={{ width: "50%" }}
                               error={errors.name}
                               helperText={errors.name} />
                </Box>

                <Box sx={{ m: 2 }}>
                    <TextField
                        label={t("forms.budget.sumForPeriod")}
                        variant="standard"
                        sx={{ width: "50%" }}
                        value={refill || ""}
                        onChange={onRefillChange}
                        error={errors.refill}
                        helperText={errors.refill}
                        InputProps={{
                            inputComponent: BalanceNumericFormat
                        }}
                    />
                    <FormControl sx={{ width: "40%", paddingLeft: "12px" }}>
                        <InputLabel id="wallet-currency-select-label"
                                    sx={{ color: errors.currency ? 'error.main' : undefined }}>{t("common.labels.currency")}</InputLabel>
                        <Select
                            value={currencyId}
                            variant="standard"
                            labelId="wallet-currency-select-labe"
                            onChange={onCurrencyChange}
                            renderValue={(selectedId) => {
                                const selectedCurrency = currencies && currencies.find(c => c.id === selectedId);
                                return selectedCurrency ? selectedCurrency.shortName : '';
                            }}
                        >
                            {currencies && currencies.map(eachCurrency =>
                                <MenuItem key={eachCurrency.id || "null"} value={eachCurrency.id}>{eachCurrency.name}({eachCurrency.shortName})</MenuItem>
                            )}
                        </Select>
                        {errors.currency && <Typography variant="caption" color="error">{errors.currency}</Typography>}
                    </FormControl>
                </Box>

                {/*START date + time*/}
                <Box sx={{ m: 2 }}>
                    <TextField
                        id="date"
                        label={t("common.labels.startDate")}
                        type="date"
                        error={errors.rule.startDate}
                        helperText={errors.rule.startDate}
                        value={startDate}
                        onChange={e => {
                            setStartDate(e.target.value);
                            setErrors(prev => ({ ...prev, rule: { ...prev.rule, startDate: null, endDate: null } }));
                        }}
                        sx={{ width: "48%" }}
                        InputLabelProps={{
                            shrink: true,
                        }}
                    />
                    <TextField
                        id="time"
                        label={t("common.labels.time")}
                        type="time"
                        error={errors.rule.time}
                        helperText={errors.rule.time}
                        value={ruleTime}
                        onChange={e => {
                            setRuleTime(e.target.value);
                            setErrors(prev => ({ ...prev, rule: { ...prev.rule, time: null } }));
                        }}
                        sx={{ width: "48%", marginLeft: "12px" }}
                        InputLabelProps={{
                            shrink: true,
                        }}
                        inputProps={{
                            step: 300,
                        }}
                    />
                </Box>
                <Box sx={{ m: 2 }}>
                    <TextField
                        id="date-end"
                        label={t("common.labels.endDate")}
                        type="date"
                        error={errors.rule.endDate}
                        helperText={errors.rule.endDate}
                        value={endDate}
                        onChange={e => {
                            setEndDate(e.target.value);
                            setErrors(prev => ({ ...prev, rule: { ...prev.rule, endDate: null } }));
                        }}
                        sx={{ width: "48%" }}
                        InputLabelProps={{
                            shrink: true,
                        }}
                    />
                </Box>

                {/*Type*/}
                <Box sx={{ m: 2 }}>
                    <FormControl variant="standard" sx={{ width: "40%", paddingLeft: "0px" }}>
                        <InputLabel id="wallet-rule-select-label"
                                    sx={{ color: errors.rule.ruleType ? 'error.main' : undefined,
                                        position: 'absolute',
                                        left: 0,
                                        transformOrigin: 'left center' }}>{t("forms.budget.replenishmentType")}</InputLabel>
                        <Select
                            value={ruleType}
                            variant="standard"
                            labelId="wallet-rule-select-label"
                            onChange={e => changeType(e.target.value)}
                            renderValue={getRuleTypeLabel}
                        >
                            <MenuItem value={RULE_TYPES.TYPE_ONE_TIME}>{getRuleTypeLabel(RULE_TYPES.TYPE_ONE_TIME)}</MenuItem>
                            <MenuItem value={RULE_TYPES.TYPE_DAILY}>{getRuleTypeLabel(RULE_TYPES.TYPE_DAILY)}</MenuItem>
                            <MenuItem value={RULE_TYPES.TYPE_WEEKLY}>{getRuleTypeLabel(RULE_TYPES.TYPE_WEEKLY)}</MenuItem>
                            <MenuItem value={RULE_TYPES.TYPE_MONTHLY}>{getRuleTypeLabel(RULE_TYPES.TYPE_MONTHLY)}</MenuItem>
                        </Select>
                        {errors.rule.ruleType && <Typography variant="caption" color="error">{errors.rule.ruleType}</Typography>}
                    </FormControl>
                </Box>

                {ruleType === RULE_TYPES.TYPE_DAILY && <Box sx={{ m: 2 }}>
                    <TextField id="dailyRule" value={dailyRule}
                               onChange={event => changeDailyRule(NumberService.getPositiveNumberStr(event.target.value))}
                               type="number"
                               label={t("forms.budget.every")}
                               error={errors.rule.dailyRule}
                               helperText={errors.rule.dailyRule}
                               variant="standard" sx={{ width: "50%" }} />
                    <div style={{ verticalAlign: "bottom", paddingLeft: 10, paddingTop: "30px", height: "50px", display: "inline-block" }}>
                        {dailyRule === "1" || dailyRule === "" ? t("forms.budget.day") : t("forms.budget.days")}
                    </div>
                </Box>}

                {ruleType === RULE_TYPES.TYPE_WEEKLY && <Box sx={{ m: 2 }}>
                    {weeklyRule.map((_, i) =>
                        <Button key={i + 1} variant="outlined"
                                sx={{
                                    borderColor: weeklyRule[i] ? "primary.main" : "primary.contrastText",
                                    padding: "2px 10px",
                                    margin: "3px",
                                    minWidth: 0,
                                    color: errors.rule.weeklyRule ? "error.main" : "primary.main"
                                }}
                                onClick={event => changeWeeklyRule(i)}>
                            {DAYS_OF_WEEK[i]}
                        </Button>
                    )}
                    {errors.rule.weeklyRule ? <Typography variant="caption" color="error">{errors.rule.weeklyRule}</Typography> :
                        <Typography variant="body1" gutterBottom>
                            {t("forms.budget.selectDaysHelp")}
                        </Typography>
                    }
                </Box>}

                {ruleType === RULE_TYPES.TYPE_MONTHLY && <Box sx={{ m: 2 }}>
                    {monthlyRule.map((_, i) =>
                        <Button key={i + 1} variant="outlined"
                                sx={{
                                    borderColor: monthlyRule[i] ? "primary.main" : "primary.contrastText",
                                    padding: "2px 10px",
                                    margin: "3px",
                                    minWidth: 0,
                                    width: 50,
                                    color: errors.rule.monthlyRule ? "error.main" : "primary.main"
                                }}
                                onClick={event => changeMonthlyRule(i)}>
                            {i + 1}
                        </Button>
                    )}
                    {errors.rule.monthlyRule ? <Typography component="div" variant="caption" color="error">{errors.rule.monthlyRule}</Typography> :
                        <Typography variant="body1" gutterBottom>
                            {t("forms.budget.selectDaysHelp")}
                        </Typography>
                    }

                </Box>}
            </CardContent>
            <CardActions sx={{ ml: "2rem", mb: "1rem", mt: "1rem", mr: "2rem", height: "4rem" }}>
                <Box sx={{ position: "relative", width: "100%" }}>
                    <Box sx={{ position: "absolute", left: 0 }}>
                        <Button variant="outlined" sx={{ mr: "1rem" }} onClick={handleCancel}>{t("common.buttons.cancel")}</Button>
                        <Button variant="contained" onClick={handleSaveLayout}>{t("common.buttons.save")}</Button>
                    </Box>
                </Box>
            </CardActions>
        </>
    );

    return changeBalanceTransaction ? (
        <EditChangeBudgetForm
            changeBudgetTransaction={changeBalanceTransaction}
            onCancel={() => { setChangeBalanceTransaction(null); }}
        />
    ) : budget ? (
        <ViewAndEditFormLayout
            editTitle={budget.id ? t("forms.budget.editTitle") : t("forms.budget.createTitle")}
            viewTitle={name}
            initialEdit={!budget.id}
            onCancel={onCancel}
            onSave={onSave}
            onDelete={budget.id ? onDelete : undefined}
            viewContent={budget.id ? viewContent : null}
            editContent={editContent}
        />
    ) : <></>
}


export default EditBudgetForm